/**
 * The in-app notification API, typed at the edge of this package.
 *
 * Every call is scoped to the signed-in person by the server — the repository
 * carries a recipient predicate in every query, and row-level security scopes
 * the tenant. Nothing here passes a user id, and nothing here should: an
 * endpoint that accepted one would be an endpoint somebody could point at
 * somebody else.
 */

import type { ApiClient } from "@hardauto/core-api";

export type NotificationLevel = "info" | "success" | "warning" | "danger";

export interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string;
  level: NotificationLevel;
  data: Record<string, unknown>;
  read_at: string | null;
  created_at: string | null;
}

export interface NotificationPage {
  notifications: Notification[];
  unread: number;
  next_cursor: string | null;
}

export interface MarkedRead {
  marked: number;
  unread: number;
}

const BASE = "/api/v1/iam/me/notifications";

export class NotificationsApi {
  readonly #client: ApiClient;

  constructor(client: ApiClient) {
    this.#client = client;
  }

  list(
    params: {
      unread_only?: boolean | undefined;
      after_id?: string | undefined;
      limit?: number | undefined;
    } = {},
  ): Promise<NotificationPage> {
    const search = new URLSearchParams();
    if (params.unread_only) search.set("unread_only", "true");
    if (params.after_id) search.set("after_id", params.after_id);
    if (params.limit) search.set("limit", String(params.limit));
    const query = search.toString();
    return this.#client.request<NotificationPage>(query ? `${BASE}?${query}` : BASE);
  }

  markRead(id: string): Promise<MarkedRead> {
    return this.#client.request<MarkedRead>(`${BASE}/${id}/read`, { method: "POST" });
  }

  markAllRead(): Promise<MarkedRead> {
    return this.#client.request<MarkedRead>(`${BASE}/read-all`, { method: "POST" });
  }
}
